import type { CardId, Move, PlayerView, Seat } from '@basgan/rules';

/**
 * İstemci ↔ sunucu protokolü.
 *
 * İKİ KANAL, bilerek ayrı:
 *
 *  `event` → NE OLDU. Ayrık, sıralı, animasyonu tetikleyen olaylar. İstemci
 *            bunları bir kuyruğa alıp sırayla oynatır; ekrandaki durum
 *            sunucunun birkaç yüz ms gerisinden gelir.
 *  `view`  → DURUM NE. Doğruluk kaynağı. Kuyruk boşalınca uygulanır;
 *            yeniden bağlanmada kuyruk atılıp doğrudan buna oturulur.
 *
 * Tek başına `view` göndermek istemciyi "kartlar neden değişti?" sorusunu
 * tersine mühendislikle çözmeye zorlardı.
 */

export interface PublicSeat {
  seat: Seat;
  name: string;
  connected: boolean;
  /** Koltuk bota devredildi mi (çıktı ya da üst üste süre aşımı). */
  bot: boolean;
  /** Bot bu koltuk adına kaç hamle oynadı — maç sonunda şeffaflık için gösterilir. */
  botMoves: number;
}

export type GameEvent =
  | { kind: 'matchStart'; seats: PublicSeat[] }
  | { kind: 'dealStart'; dealIndex: number; leader: Seat }
  | { kind: 'play'; seat: Seat; cards: CardId[] }
  | { kind: 'pass'; seat: Seat }
  | { kind: 'cut'; seat: Seat }
  | { kind: 'trickEnd'; winner: Seat; nextLeader: Seat }
  | { kind: 'finish'; seat: Seat; place: number }
  | { kind: 'dealEnd'; order: Seat[]; scores: number[] }
  /** Zorunlu yön taşındı; kartların kimden kime uçacağını animasyon buradan bilir. */
  | { kind: 'exchangeStart'; transfers: { from: Seat; to: Seat; count: number }[] }
  | { kind: 'exchangeDone' }
  | { kind: 'presence'; seat: Seat; connected: boolean; bot: boolean }
  | { kind: 'chat'; seat: Seat; text: string }
  /**
   * Yönetici maskotu saldı — masadaki HERKES aynı anda görsün.
   *
   * Yükü yok: ne zaman koşacağından başka söylenecek bir şey yok, tur da
   * istemcide sabit. Kuyruğa girmiyor (`eventDuration` 0), yani oyunun
   * akışını bekletmiyor; maskot masanın üstünde kendi başına oynayıp gidiyor.
   */
  | { kind: 'mascot' }
  /** Maç sonunda masada kalmayı seçen koltuk. */
  | { kind: 'stayed'; seat: Seat }
  /** Maç sonunda masadan kalkan koltuk — "şerefsizi sattı". */
  | { kind: 'sold'; seat: Seat; name: string }
  | {
      kind: 'matchOver';
      standings: { seat: Seat; score: number; place: number }[];
      /**
       * "Kal ya da sat" kararının son anı (epoch ms).
       *
       * Yalnızca ÇEVRİMİÇİ masada var: botlara karşı oynarken kimse kimseyi
       * beklemiyor, ekranda geri sayan bir süre göstermek gereksiz baskı olur.
       */
      decideBy?: number;
    };

/**
 * Hazır mesajların GÖMÜLÜ yedeği.
 *
 * Tek doğru kaynak değil: sunucu kendi listesini bir dosyadan okuyup katılan
 * istemciye `phrases` mesajıyla gönderiyor, böylece cümleler yeni sürüm
 * çıkmadan değiştirilebiliyor. Bu liste iki durumda kullanılıyor — sunucuda
 * dosya yoksa ve istemci botlarla OFFLINE oynarken (o modda sunucu yok).
 *
 * Neden hazır liste: klavye açmadan tek dokunuşla çalışıyor (yatay telefonda
 * klavye ekranın yarısını yiyor) ve serbest metin olmadığı için moderasyon
 * derdi yok. Mesaj METİN olarak taşınıyor, indeks olarak değil: liste
 * değişebildiği için indeks sunucu ile istemcide farklı cümleye denk gelip
 * herkesin ekranında yanlış mesaj gösterebilirdi.
 */
export const FALLBACK_CHAT_PHRASES: readonly string[] = [
  'Selam beyler',
  'Karo 2 kimde?',
  'Düşşüğumu gelecem.',
  'Vallaha bak.',
  'Bırak la.',
  'Adamı sikerler işte böyle.',
  'Senin elinde ne var la!',
  'Var mı götü yiyen?',
  'Kim başlıyor şimdi?',
  'Sonuncu, bide yap!',
  'Buranın kralı benim?',
  'Sen ancak veliaht olursun.',
  'İyi ki gelmişim la',
  'Hep bana oynuyorsunuz.',
  'Bana uylamayın',
  'Yihh!',
  'Ver lan şunu salah',
  'Mal la bu',
  'Yaz la 500 yaz. 📞',
  'Sahlep ama çay bardağında',
  'Abaa, tek kartı kalmış la',
  'Alıyım! ☕',
  '5 çay yaz.',
  'Abi bana kola.',
];

/**
 * Bir olayın ekranda ne kadar süreceği (ms).
 * Sıra tabanlı oyunda uzun animasyon bekletir — kısa tutup okunur bırakıyoruz.
 *
 * Burada, protokolde duruyor çünkü İKİ TARAF da kullanıyor: istemci olayları
 * bu tempoda oynatıyor, sunucu da sıra zamanlayıcısını buna göre geciktiriyor.
 * Yalnızca istemcide dururken sunucu tempoyu bilmiyordu ve bot, kartlar daha
 * dağıtılırken oynuyordu.
 */
export function eventDuration(event: GameEvent): number {
  switch (event.kind) {
    case 'play':
      // Yay animasyonunun oturma süresiyle hizalı (~420 ms). Daha kısa olursa
      // rakibin kartı masaya inmeden sıradaki olay başlıyor.
      return 420;
    case 'pass':
      return 260;
    case 'cut':
      // Ayrı bir görseli yok (KESTİ duyurusu kaldırıldı); atılan 2 görülecek
      // kadar kısa bir duraklama yetiyor.
      return 320;
    case 'trickEnd':
      return 480;
    case 'finish':
      return 520;
    case 'dealEnd':
      return 2400; // el sonu özeti okunacak kadar dursun
    case 'exchangeStart':
      return 1100; // kartların oyuncular arası uçuşu
    case 'exchangeDone':
      // Kısa: sonuç perdesi artık kuyruğa değil kendi sayacına bağlı. Uzun
      // tutulduğunda görünüm bu süre boyunca uygulanmıyor ve ekranda hâlâ
      // "karşı taraf bekleniyor" yazıyordu — oysa değişim çoktan bitmişti.
      return 250;
    case 'dealStart':
      return 700; // dağıtım
    case 'matchStart':
      // Görsel karşılığı yok (indirgeyici durumu değiştirmiyor); beklemek
      // geri sayım bittikten sonra ölü bir 400 ms yaratıyordu.
      return 0;
    case 'matchOver':
      return 0;
    case 'presence':
      return 0; // bilgilendirme, akışı bekletmesin
    case 'mascot':
    case 'stayed':
    case 'sold':
      // Üçü de kuyruğun DIŞINDA işleniyor (`useGame` araya giriyor): maskot
      // masanın üstünde kendi başına koşuyor, kal/sat kararları da maç
      // bittikten sonra geliyor — bekletilecek bir animasyon kalmıyor.
      return 0;
    case 'chat':
      // Sohbet kuyruğa hiç girmiyor (`useGame` araya giriyor); yine de
      // indirgeyici bütün olay türlerini karşılamak zorunda.
      return 0;
  }
}

/** Oyuncuya özel durum. `PlayerView` rakip ellerini zaten içermiyor. */
export type ViewMessage = PlayerView & {
  /** Artan sıra numarası. İstemci geç gelen eski görünümü atar. */
  seq: number;
  you: Seat;
  seats: PublicSeat[];
  /** Sıra sahibinin süresinin dolacağı an (epoch ms); yoksa null. */
  deadline: number | null;
  /**
   * O sürenin tamamı (ms) — geri sayım halkasının ne kadar dolu çizileceği.
   * İstemci sabit bir sayı varsayamaz: hamle süresi, değişim süresi ve bot
   * düşünme süresi farklı. Sunucu tek doğru kaynak.
   */
  deadlineTotal: number | null;
  /**
   * Bu istemci masanın yöneticisi mi.
   *
   * `LobbyMessage` içindeki alanın maç sürerkenki karşılığı: maskotu yalnızca
   * yönetici salabildiği için düğmenin kime görüneceği masada da bilinmeli.
   */
  host?: boolean;
};

export interface LobbyMessage {
  players: { name: string; ready: boolean; bot?: boolean }[];
  needed: number;
  /**
   * Bu istemci masanın yöneticisi mi.
   *
   * Yönetici, masaya İLK OTURAN insan. Boş koltuklara bot ekleyip
   * çıkarabilen tek kişi o. Alan istemciye göre değiştiği için `lobby`
   * mesajı herkese aynı gönderilemiyor, sokete tek tek yazılıyor.
   */
  host?: boolean;
}

/** İstemciden gelen mesajlar. */
export interface ClientMessages {
  move: Move;
  exchange: { cards: CardId[] };
  /**
   * Hazır mesaj metni. Sunucu kendi listesinde var mı diye bakıyor —
   * yani hâlâ beyaz liste, serbest metin kabul edilmiyor.
   */
  chat: { text: string };
  /** Dinleyiciler bağlandı; masanın durumunu ve hazır mesaj listesini iste. */
  hello: Record<string, never>;
  /**
   * Masadaki bot koltuğu sayısını AYARLAR — artırmaz, mutlak değer verir.
   *
   * Ekleme ve çıkarma tek mesajda toplanıyor: sayı mutlak olduğu için
   * mesajların sırası karışsa da masa aynı yere varıyor. Yalnızca yönetici
   * gönderebilir; sunucu gönderene bakıp sessizce yok sayıyor.
   */
  bots: { count: number };
  /** Maç bitti, masada kalıyorum — dördü de kalınca yeni maç geri sayımı başlar. */
  stay: Record<string, never>;
  /**
   * Maç bitti, masadan kalkıyorum ("şerefsizi sattım").
   *
   * Bağlantıyı kapatmak TEK BAŞINA yetmiyor: sunucu kapanmayı görür ama
   * kalanların ekranında kimin sattığını yazamaz. Bu mesaj duyuruyu
   * tetikliyor, kapanış hemen ardından geliyor.
   */
  sell: Record<string, never>;
  /**
   * Maskotu masaya sal. Yalnızca yönetici gönderebilir; sunucu gönderene
   * bakıp sessizce yok sayıyor ve arka arkaya salınmasını da engelliyor.
   */
  mascot: Record<string, never>;
}

/** Sunucunun yürürlükteki hazır mesaj listesi — katılışta gönderiliyor. */
export interface PhrasesMessage {
  phrases: string[];
}
export * from './derive.js';
